/**
 * Measure Units Utility
 * Provides measurement unit options and helper functions for invoices and products
 */

import i18n from '../../i18n';

const translateMeasureUnit = (value) =>
  i18n.t(`common.measure_units.${value}`, { defaultValue: value });

const MEASURE_UNIT_DEFS = [
  // ========== QUANTITY / COUNTING ==========
  { value: 'pcs', category: 'quantity', symbol: 'pcs' },
  { value: 'unit', category: 'quantity', symbol: 'u' },
  { value: 'set', category: 'quantity', symbol: 'set' },
  { value: 'box', category: 'quantity', symbol: 'box' },
  { value: 'pack', category: 'quantity', symbol: 'pack' },
  { value: 'carton', category: 'quantity', symbol: 'ctn' },
  { value: 'palette', category: 'quantity', symbol: 'plt' },
  { value: 'container', category: 'quantity', symbol: 'cont' },
  { value: 'dozen', category: 'quantity', symbol: 'dz' },
  { value: 'pair', category: 'quantity', symbol: 'pr' },

  // ========== WEIGHT / MASS ==========
  { value: 'kg', category: 'weight', symbol: 'kg' },
  { value: 'g', category: 'weight', symbol: 'g' },
  { value: 'mg', category: 'weight', symbol: 'mg' },
  { value: 't', category: 'weight', symbol: 't' },
  { value: 'q', category: 'weight', symbol: 'q' },
  { value: 'lb', category: 'weight', symbol: 'lb' },
  { value: 'oz', category: 'weight', symbol: 'oz' },

  // ========== VOLUME / CAPACITY ==========
  { value: 'l', category: 'volume', symbol: 'L' },
  { value: 'ml', category: 'volume', symbol: 'mL' },
  { value: 'hl', category: 'volume', symbol: 'hL' },
  { value: 'm3', category: 'volume', symbol: 'm³' },
  { value: 'cm3', category: 'volume', symbol: 'cm³' },
  { value: 'gal', category: 'volume', symbol: 'gal' },
  { value: 'barrel', category: 'volume', symbol: 'bbl' },

  // ========== LENGTH / DISTANCE ==========
  { value: 'm', category: 'length', symbol: 'm' },
  { value: 'cm', category: 'length', symbol: 'cm' },
  { value: 'mm', category: 'length', symbol: 'mm' },
  { value: 'km', category: 'length', symbol: 'km' },
  { value: 'in', category: 'length', symbol: 'in' },
  { value: 'ft', category: 'length', symbol: 'ft' },
  { value: 'yd', category: 'length', symbol: 'yd' },

  // ========== AREA / SURFACE ==========
  { value: 'm2', category: 'area', symbol: 'm²' },
  { value: 'cm2', category: 'area', symbol: 'cm²' },
  { value: 'ha', category: 'area', symbol: 'ha' },
  { value: 'acre', category: 'area', symbol: 'acre' },
  { value: 'ft2', category: 'area', symbol: 'ft²' },

  // ========== TIME / DURATION ==========
  { value: 'hour', category: 'time', symbol: 'h' },
  { value: 'day', category: 'time', symbol: 'j' },
  { value: 'week', category: 'time', symbol: 'sem' },
  { value: 'month', category: 'time', symbol: 'mois' },
  { value: 'year', category: 'time', symbol: 'an' },
  { value: 'minute', category: 'time', symbol: 'min' },

  // ========== ENERGY / POWER ==========
  { value: 'kwh', category: 'energy', symbol: 'kWh' },
  { value: 'mwh', category: 'energy', symbol: 'MWh' },
  { value: 'kw', category: 'energy', symbol: 'kW' },
  { value: 'w', category: 'energy', symbol: 'W' },

  // ========== DIGITAL / DATA ==========
  { value: 'gb', category: 'digital', symbol: 'GB' },
  { value: 'mb', category: 'digital', symbol: 'MB' },
  { value: 'tb', category: 'digital', symbol: 'TB' },

  // ========== SERVICE / ABSTRACT ==========
  { value: 'service', category: 'service', symbol: 'srv' },
  { value: 'session', category: 'service', symbol: 'session' },
  { value: 'course', category: 'service', symbol: 'course' },
  { value: 'license', category: 'service', symbol: 'lic' },
  { value: 'subscription', category: 'service', symbol: 'sub' },
];

const withTranslatedLabel = (def) => ({
  ...def,
  label: translateMeasureUnit(def.value),
});

/**
 * Standard measurement unit options for products and services
 * Organized by category: Quantity, Weight, Volume, Length, Area, Time, Digital
 */
export const MEASURE_UNIT_OPTIONS = MEASURE_UNIT_DEFS.map(withTranslatedLabel);

export const getMeasureUnitOptions = () => MEASURE_UNIT_DEFS.map(withTranslatedLabel);

/**
 * Get a select-compatible value for a measure unit
 * Handles both string values and object values from CustomSelect
 *
 * @param {string|object} unit - The unit value (can be string or {value, label} object)
 * @returns {object|null} - Select-compatible object {value, label} or null
 */
export function measureUnitSelectValue(unit) {
  if (!unit) return null;

  // If already an object with value/label, return as-is
  if (typeof unit === 'object' && unit.value && unit.label) {
    return unit;
  }

  // If it's a string, find matching option
  if (typeof unit === 'string') {
    const found = MEASURE_UNIT_DEFS.find(
      (opt) =>
        opt.value === unit ||
        opt.value.toLowerCase() === unit.toLowerCase()
    );

    if (found) {
      return withTranslatedLabel(found);
    }

    // If not found in options, create custom option
    return {
      value: unit,
      label: unit,
      category: 'custom',
      symbol: unit,
    };
  }

  return null;
}

/**
 * Canonicalize a measure unit to a standard format
 * Converts various input formats to a consistent string value
 *
 * @param {string|object} unit - The unit to canonicalize
 * @returns {string} - Canonical unit string (lowercase)
 */
export function canonicalizeMeasureUnit(unit) {
  if (!unit) return 'pcs'; // Default to pieces

  // If it's an object (from CustomSelect), extract value
  if (typeof unit === 'object' && unit.value) {
    return String(unit.value).toLowerCase();
  }

  // If it's a string, return lowercase
  if (typeof unit === 'string') {
    return unit.toLowerCase();
  }

  return 'pcs'; // Fallback default
}

/**
 * Format a measure unit for display (human-readable)
 * Returns the label if available, otherwise the symbol or value
 *
 * @param {string|object} unit - The unit to format
 * @param {boolean} useSymbol - Whether to use symbol instead of full label
 * @returns {string} - Formatted unit string
 */
export function formatMeasureUnitForDisplay(unit, useSymbol = false) {
  if (!unit) return translateMeasureUnit('pcs');

  // If it's an object with label
  if (typeof unit === 'object' && (unit.label || unit.value)) {
    if (useSymbol && unit.symbol) {
      return unit.symbol;
    }
    if (unit.value) {
      return translateMeasureUnit(unit.value);
    }
    return unit.label || unit.value;
  }

  // If it's a string, try to find in options
  if (typeof unit === 'string') {
    const found = MEASURE_UNIT_DEFS.find(
      (opt) =>
        opt.value === unit ||
        opt.value.toLowerCase() === unit.toLowerCase()
    );

    if (found) {
      return useSymbol ? found.symbol : translateMeasureUnit(found.value);
    }

    // Return as-is if not found
    return unit;
  }

  return translateMeasureUnit('pcs'); // Fallback
}

/**
 * Get measure unit symbol (short version)
 *
 * @param {string|object} unit - The unit
 * @returns {string} - Symbol (e.g., "kg", "L", "m²")
 */
export function getMeasureUnitSymbol(unit) {
  return formatMeasureUnitForDisplay(unit, true);
}

/**
 * Check if a measure unit is valid
 *
 * @param {string|object} unit - The unit to validate
 * @returns {boolean} - Whether the unit is valid
 */
export function isValidMeasureUnit(unit) {
  if (!unit) return false;

  const canonical = canonicalizeMeasureUnit(unit);
  const found = MEASURE_UNIT_DEFS.find(
    (opt) => opt.value.toLowerCase() === canonical.toLowerCase()
  );

  return !!found || !!canonical; // Valid if found in options or is a non-empty string
}

/**
 * Get all measure units in a specific category
 *
 * @param {string} category - Category name (e.g., 'weight', 'volume', 'quantity')
 * @returns {array} - Array of unit options in that category
 */
export function getMeasureUnitsByCategory(category) {
  return getMeasureUnitOptions().filter((opt) => opt.category === category);
}

/**
 * Get all available categories
 *
 * @returns {array} - Array of unique category names
 */
export function getMeasureUnitCategories() {
  const categories = new Set();
  MEASURE_UNIT_DEFS.forEach((opt) => {
    if (opt.category) {
      categories.add(opt.category);
    }
  });
  return Array.from(categories);
}

// Default export
export default {
  MEASURE_UNIT_OPTIONS,
  getMeasureUnitOptions,
  measureUnitSelectValue,
  canonicalizeMeasureUnit,
  formatMeasureUnitForDisplay,
  getMeasureUnitSymbol,
  isValidMeasureUnit,
  getMeasureUnitsByCategory,
  getMeasureUnitCategories,
};
